/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Popup } from '@atlaskit/popup/popup';

import type { CyphieMode } from '../../../../../../../state/LabsAtoms';
import { messages } from './messages';
import { ModeIcon } from './ModeIcon';
import { MODE_LABELS, PopupContent } from './PopupContent';

type ModeSelectorPopupProps = {
	selectedMode: CyphieMode;
	external?: boolean;
	onModeChange: (mode: CyphieMode) => void;
	testId?: string;
};

export const ModeSelectorPopup = ({
	selectedMode,
	external = false,
	onModeChange,
	testId = 'mode-selector-popup',
}: ModeSelectorPopupProps): JSX.Element => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const { formatMessage } = useIntl();
	const selectedModeLabel = formatMessage(
		external && selectedMode === 'default' ? messages.fastLabel : MODE_LABELS[selectedMode],
	);

	const handleModeSelect = useCallback(
		(mode: CyphieMode) => {
			onModeChange(mode);
			setIsMenuOpen(false);
		},
		[onModeChange],
	);

	return (
		<Popup
			shouldRenderToParent
			isOpen={isMenuOpen}
			onClose={() => setIsMenuOpen(false)}
			placement="top-end"
			content={() => (
				<PopupContent
					external={external}
					selectedMode={selectedMode}
					onModeSelect={handleModeSelect}
					testId={`${testId}-content`}
				/>
			)}
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					appearance="subtle"
					onClick={() => setIsMenuOpen(!isMenuOpen)}
					aria-label={formatMessage(messages.selectedModelAriaLabel, {
						mode: selectedModeLabel,
					})}
					iconBefore={(iconProps) => <ModeIcon mode={selectedMode} {...iconProps} />}
					iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} />}
				>
					{selectedModeLabel}
				</Button>
			)}
		/>
	);
};
